// ============================================================================
// APP VARIANT — build-time product flavor
// ============================================================================
// The Teaching Assistant (for teachers) and the General Assistant (for MOE
// employees) are the SAME codebase, deployed as two separate Netlify sites.
// The only difference between the two builds is the REACT_APP_APP_VARIANT
// environment variable, baked in at build time by Create React App.
//
//   - unset / 'teacher'  → Teaching Assistant   (default, existing product)
//   - 'general'          → General Assistant     (MOE employees; no school /
//                                                 curriculum / homework / exam)
//
// How it is set:
//   - Local dev:  set REACT_APP_APP_VARIANT=general before `npm start`
//   - Netlify:    add REACT_APP_APP_VARIANT=general in the GENERAL site's
//                 environment variables (NOT in netlify.toml — that file is
//                 shared by both sites). See GENERAL_ASSISTANT.md.
//
// Nothing here is secret; the flag only chooses which UI/nav/branding ships.
// ============================================================================

export const VARIANT = (process.env.REACT_APP_APP_VARIANT || 'teacher')
  .trim()
  .toLowerCase();

export const isGeneral = VARIANT === 'general';

// Sent on outbound n8n webhook payloads so the SAME workflow can branch its
// system prompt (teacher vs. general office assistant) with minimal backend
// work. Existing workflows that ignore this field keep working unchanged.
export const APP_VARIANT = isGeneral ? 'general' : 'teacher';

// ---------------------------------------------------------------------------
// Per-variant configuration.
// ---------------------------------------------------------------------------
const CONFIG = {
  teacher: {
    id: 'teacher',
    // Canonical route for the shared document editor in this variant.
    documentPath: '/homework-generator',
    // appName is also overridden via the locale files (common.appName); this is
    // the source of truth for document.title and any non-i18n usage.
    appName: { en: 'Teaching Assistant', ar: 'المساعد التعليمي' },
    // The page's meta description (set at runtime by LanguageContext).
    description: { en: 'AI-Powered Teaching Assistant', ar: 'مساعد تعليمي مدعوم بالذكاء الاصطناعي' },
    // Feature flags — control which routes exist and which UI sections render.
    features: {
      schoolBooks: true,        // Knowledge Base → "School Books" tab
      dataSources: true,        // Knowledge Base → "Data Sources" tab (both variants)
      homeworkGenerator: true,  // shared document editor (kept in both)
      homeworkCorrector: true,
      contentGenerator: true,   // worksheets / lesson plans / activities / …
      deepAnalysis: true,       // student indicators
      dataAnalysis: true,
      voiceAssistant: true,
      tasks: false,           // Tasks & follow-ups (both variants)
      // Tab hidden 2026-09-14: no sidebar entry, no route. The chat still
      // answers scholarship questions; set true to bring the tab back.
      scholarships: false,    // Scholarships / الابتعاث (both variants)
    },
    // Sidebar "HELPERS" section — consolidated (reorganized) layout: the two
    // homework routes collapse into one "Assignments & Exams" entry and the two
    // analytics routes into one "Data Analysis" entry. The in-page FeatureTabs
    // bar switches between the merged sub-routes.
    helpers: [
      {
        id: 'assignments', path: '/homework-generator',
        matchPaths: ['/homework-generator', '/homework-corrector'],
        icon: 'edit_document',
        nameEn: 'Assignments & Exams', nameAr: 'الواجبات والامتحانات',
        descEn: 'Create & auto-grade', descAr: 'الإنشاء والتصحيح التلقائي',
      },
      {
        id: 'content-generator',
        path: '/content-generator',
        matchPaths: [
          '/content-generator',
          '/activities',
          '/worksheet',
          '/lesson-plan',
          '/presentation',
          '/suggested-videos'
        ],
        icon: 'book_2',
        nameEn: 'Teaching Content Generator',
        nameAr: 'منشئ المحتوى التعليمي',
        descEn: 'Create teaching materials',
        descAr: 'إنشاء المواد التعليمية',
      },
      {
        id: 'tasks', path: '/tasks', feature: 'tasks', icon: 'checklist',
        nameEn: 'Tasks', nameAr: 'المهام',
        descEn: 'Follow-ups & assignments', descAr: 'المتابعات والتكليفات',
      },
      {
        id: 'scholarships', path: '/scholarships', feature: 'scholarships', icon: 'school',
        nameEn: 'Scholarships', nameAr: 'الابتعاث',
        descEn: 'Eligibility & pipeline', descAr: 'الأهلية والمسار',
      },
      {
        id: 'analytics', path: '/deep-analysis',
        matchPaths: ['/deep-analysis', '/data-analysis'],
        icon: 'pie_chart',
        nameEn: 'Data Analysis', nameAr: 'تحليل البيانات',
        descEn: 'Performance & data analysis', descAr: 'تحليل الأداء والبيانات',
      },
    ],
  },

  general: {
    id: 'general',
    // Neutral route so the URL doesn't expose the teacher origin.
    documentPath: '/documents',
    appName: { en: 'MOE Assistant', ar: 'مساعد الموظف' },
    // The page's meta description (set at runtime by LanguageContext).
    description: { en: 'AI-powered assistant for Ministry of Education staff', ar: 'مساعد مدعوم بالذكاء الاصطناعي لمنسوبي وزارة التعليم' },
    // The general employee assistant intentionally drops everything that is
    // tied to a school / curriculum: homework, exams, grading, lesson plans,
    // worksheets, student analytics, and school books.
    features: {
      schoolBooks: false,
      // Institutional statistics databases are, if anything, more of an
      // MOE-employee job than a teacher one — enabled in both variants.
      dataSources: true,
      homeworkGenerator: true,   // reused as a generic "Document Assistant"
      homeworkCorrector: false,
      contentGenerator: false,
      deepAnalysis: false,
      dataAnalysis: true,
      voiceAssistant: true,
      tasks: false,
      // Tab hidden 2026-09-14: no sidebar entry, no route. The chat still
      // answers scholarship questions; set true to bring the tab back.
      scholarships: false,
    },
    helpers: [
      {
        id: 'document',
        path: '/documents',
        matchPaths: ['/documents', '/homework-generator'],
        icon: 'description',
        nameEn: 'Document Assistant', nameAr: 'مساعد المستندات',
        descEn: 'Draft, edit & export documents', descAr: 'صياغة وتحرير وتصدير المستندات',
      },
      {
        id: 'tasks', path: '/tasks', feature: 'tasks', icon: 'checklist',
        nameEn: 'Tasks', nameAr: 'المهام',
        descEn: 'Follow-ups & assignments', descAr: 'المتابعات والتكليفات',
      },
      {
        id: 'scholarships', path: '/scholarships', feature: 'scholarships', icon: 'school',
        nameEn: 'Scholarships', nameAr: 'الابتعاث',
        descEn: 'Eligibility & pipeline', descAr: 'الأهلية والمسار',
      },
      {
        id: 'analytics',
        path: '/data-analysis',
        matchPaths: ['/data-analysis'],
        icon: 'pie_chart',
        nameEn: 'Data Analysis',  nameAr: 'تحليل البيانات',
        descEn: 'Analyze any dataset', descAr: 'تحليل أي مجموعة بيانات',
      },
    ],
  },
};

// A helper tagged with `feature` only appears when that flag is on, so one flag
// hides a tool's sidebar entry as well as its route (App.js checks the same flags).
const selected = CONFIG[VARIANT] || CONFIG.teacher;

export const variantConfig = {
  ...selected,
  helpers: selected.helpers.filter((h) => !h.feature || selected.features[h.feature]),
};

export default variantConfig;
